import { AnimatePresence, motion } from 'motion/react';
import * as React from 'react';
import { cn } from '~/lib/utils';

type FlipWordsProps = {
  words: string[];
  duration?: number;
  className?: string;
};

export function FlipWords({ words, duration = 2000, className }: FlipWordsProps) {
  const [currentWord, setCurrentWord] = React.useState(words[0]);
  const [isAnimating, setIsAnimating] = React.useState<boolean>(false);

  const startAnimation = React.useCallback(() => {
    const word = words[words.indexOf(currentWord) + 1] || words[0];
    setCurrentWord(word);
    setIsAnimating(true);
  }, [currentWord, words]);

  React.useEffect(() => {
    if (!isAnimating)
      setTimeout(() => {
        startAnimation();
      }, duration);
  }, [isAnimating, duration, startAnimation]);

  return (
    <AnimatePresence onExitComplete={() => setIsAnimating(false)}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 10 }}
        exit={{ opacity: 0, y: -40, x: 40, filter: 'blur(8px)', scale: 2, position: 'absolute' }}
        className={cn('z-10 inline-block relative text-left px-2 pr-10 text-white/80', className)}
        key={currentWord}
      >
        {currentWord.split(' ').map((word, wordIndex) => (
          <motion.span
            key={word + wordIndex}
            initial={{ opacity: 0, y: 10, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ delay: wordIndex * 0.3, duration: 0.3 }}
            className="whitespace-nowrap gradient-text-orange"
          >
            {word.split('').map((letter, letterIndex) => (
              <motion.span
                key={word + letterIndex}
                className={cn('inline-block', getLetterClassName(word.length, letterIndex))}
              >
                {letter}
              </motion.span>
            ))}
            <span className="inline-block">&nbsp;&nbsp;</span>
          </motion.span>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

function getLetterClassName(wordLength: number, letterIndex: number) {
  const positionFromEnd = wordLength - letterIndex - 1;
  return positionFromEnd < 2 ? '' : '';
}
